/**
 * Generic schema validation utility
 * @param {Object} schema - Yup validation schema
 * @param {Object} data - Data object to validate
 * @param {Object} errorMessages - Object containing error message translations (e.g., inputsValidations)
 * @returns {Object} - Returns empty object if valid, or object with field errors
 *
 * @example
 * const errors = validateSchema(mySchema, formData, inputsValidations);
 * if (Object.keys(errors).length > 0) {
 *   setValidationErrors(errors);
 *   return;
 * }
 */
export const validateSchema = (schema, data, errorMessages = {}) => {
  try {
    schema.validateSync(data, { abortEarly: false });
    return {};
  } catch (err) {
    const errors = {};
    if (err.inner) {
      err.inner.forEach((error) => {
        errors[error.path] = errorMessages[error.message] || error.message;
      });
    }
    return errors;
  }
};

/**
 * Validate a single field from a schema
 * @param {Object} schema - Full Yup validation schema
 * @param {string} fieldName - Name of the field to validate
 * @param {any} value - Value to validate
 * @param {Object} errorMessages - Object containing error message translations
 * @returns {string} - Error message or empty string if valid
 *
 * @example
 * const error = validateField(mySchema, "email", emailValue, inputsValidations);
 * setErrors(prev => ({ ...prev, email: error }));
 */
export const validateField = (schema, fieldName, value, errorMessages = {}) => {
  const errors = validateSchema(
    schema.pick([fieldName]),
    { [fieldName]: value },
    errorMessages
  );
  return errors[fieldName] || "";
};

/**
 * Check if data is valid against a schema
 * @param {Object} schema - Yup validation schema
 * @param {Object} data - Data object to validate
 * @returns {boolean} - true if valid, false if has errors
 *
 * @example
 * const isValid = isSchemaValid(mySchema, formData);
 */
export const isSchemaValid = (schema, data) => {
  try {
    schema.validateSync(data, { abortEarly: true });
    return true;
  } catch {
    return false;
  }
};